import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { adminLogin } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (newPassword !== confirmPassword) { setError("Las contraseñas no coinciden."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/auth/reset-password`, { token, new_password: newPassword });
      adminLogin(res.data.token, res.data.user);
      navigate("/admin");
    } catch (err) {
      setError(translateAdminError(err.response?.data?.detail) || "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="admin-reset-password-page">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-sm text-slate-700">Enlace inválido. Solicitá uno nuevo.</p>
          <Link to="/admin/forgot-password" className="text-xs text-[#028CF8] font-medium hover:underline mt-3 inline-block">Solicitar enlace</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="admin-reset-password-page">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#028CF8] flex items-center justify-center mb-3">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-bold text-lg text-slate-900">Nueva contraseña</h1>
        </div>
        {error && <div data-testid="admin-reset-password-error" className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Nueva contraseña</label>
            <input data-testid="admin-reset-new-password-input" type="password" required value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Confirmar contraseña</label>
            <input data-testid="admin-reset-confirm-password-input" type="password" required value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]" />
          </div>
          <button type="submit" data-testid="admin-reset-password-submit" disabled={loading}
            className="w-full h-11 rounded-lg bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restablecer contraseña"}
          </button>
        </form>
      </div>
    </div>
  );
}
